import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { useModulePermissions } from "../../context/ModulePermissionsContext";

interface PermissionedRouteProps {
  children: React.ReactNode;
  module: string;
}

export function PermissionedRoute({
  children,
  module,
}: PermissionedRouteProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } =
    useModulePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const hasChecked = useRef(false);

  useEffect(() => {
    const checkAccess = async () => {
      // Prevent multiple checks
      if (hasChecked.current) {
        return;
      }

      // Wait for both auth and permissions to load
      if (authLoading || permissionsLoading) {
        return;
      }

      // First check authentication
      if (!isAuthenticated) {
        hasChecked.current = true;
        navigate("/login", {
          replace: true,
          state: { from: location },
        });
        return;
      }

      // Then check module permission
      if (!hasPermission(module)) {
        hasChecked.current = true;
        // Only redirect to dashboard if we're not already there
        if (location.pathname !== "/dashboard") {
          navigate("/dashboard", { replace: true });
        }
        return;
      }

      hasChecked.current = true;
    };

    checkAccess();
  }, [
    isAuthenticated,
    authLoading,
    permissionsLoading,
    hasPermission,
    module,
    navigate,
    location,
  ]);

  // Show a lightweight loading shell instead of rendering nothing (blank screen).
  if (authLoading || permissionsLoading) {
    return (
      <div
        className="flex h-full min-h-[12rem] items-center justify-center text-slate-500 dark:text-slate-400"
        data-testid="permissioned-route-loading"
      >
        Loading…
      </div>
    );
  }

  // Only render children when authenticated and has permission
  return isAuthenticated && hasPermission(module) ? children : null;
}
